import React, { useEffect, useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { LockIcon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { usePlatform } from "~/hooks/use-platform";
import { usePermissionManager } from "~/managers/use-permission-manager";
import { usePermissionStore } from "~/stores/use-permission-store";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { MemoizedKeypadInput } from "../ui/keypad-input";

export default function EmployeeSignIn() {
  const { t } = useTranslation("common");

  const { login } = usePermissionManager();

  const { signInOpen, cancelSignIn, signInLabel, isSettingTag } =
    usePermissionStore();

  const [rfidData, setRfidData] = useState("");

  const [lastKeyPressTime, setLastKeyPressTime] = useState(Date.now());

  const { platform } = usePlatform();

  useEffect(() => {
    if (platform === "DESKTOP" && !isSettingTag) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (
          event.target instanceof HTMLInputElement ||
          event.target instanceof HTMLTextAreaElement
        ) {
          return;
        }
        const currentTime = Date.now();
        const isRFIDInput = (ct: number, lkpt: number) => {
          const timeDiff = ct - lkpt;
          return timeDiff < 50 || lkpt === 0;
        };

        if (isRFIDInput(currentTime, lastKeyPressTime)) {
          event.preventDefault();
          if (event.key === "Enter") {
            login(rfidData.trim(), true);
            setRfidData("");
          } else {
            setRfidData((prev) => prev + event.key);
          }
        } else {
          setRfidData(event.key);
        }

        setLastKeyPressTime(currentTime);
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [login, lastKeyPressTime, isSettingTag, platform, rfidData]);

  return (
    <Dialog
      open={signInOpen}
      onOpenChange={(open) => {
        if (open === false) {
          cancelSignIn();
        }
      }}
    >
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="p-0 rounded-full z-[9999999]"
        overlayClassName="z-[9999999]"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>{t("login")}</DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>
        </VisuallyHidden>
        <DialogClose asChild>
          <Button
            className="absolute top-2 right-2 p-1 rounded-full w-8 h-8"
            variant="ghost"
          >
            <XIcon />
          </Button>
        </DialogClose>

        <MemoizedKeypadInput
          icon={<LockIcon className="text-white" />}
          className="bg-red-500"
          label={t("Permissions.enter_pin")}
          description={signInLabel ?? t("Permissions.enter_pin_description")}
          onChange={login}
          type="password"
        />
      </DialogContent>
    </Dialog>
  );
}
